/*
 * Client-side diagnostic breadcrumb buffer.
 *
 * Captures nav + api + state + exception events in a rolling FIFO buffer,
 * then ships them to the API when a notable event fires (forced logout,
 * error boundary, 5xx, network error, pagination failure, manual report).
 *
 * Gated by a per-investigation flag fetched from the API. Zero-deps, no side
 * effects at import time — `initDiagnostics()` must be called before any
 * `addBreadcrumb()` takes effect.
 *
 * Production-safe: redacts payloads against a strict allowlist; never logs
 * patient names/DOB/personalData; bounded in size (events + bytes); uses
 * fetch keepalive so a forced-logout flush still lands during navigation.
 */

import {
    apiBaseUrl as inferApiBaseUrl,
    jwt as safeGetAuthToken,
    decodeJwtClaims,
    safeRandomUUID,
} from './identity';

const DEFAULT_CONFIG = {
    maxBufferSize: 100,
    maxBufferBytes: 100 * 1000,
    flushRateLimitMs: 10 * 1000,
};

const ALLOWED_DATA_KEYS = new Set([
    'url',
    'method',
    'status',
    'durationMs',
    'correlationId',
    'requestId',
    'code',
    'errorName',
    'errorMessage',
    'route',
    'uuid',
    'permissionKey',
    'jwtExp',
    'jwtIat',
    'jwtIatMs',
    'researcherUuid',
    'pageOffset',
    'pageLimit',
    'pageNumber',
    'pageSize',
    'offset',
    'limit',
    'returnedCount',
    'total',
    'hasMore',
    'atOffset',
    'pagesCompleted',
    'memoryUsedMB',
    'memoryLimitMB',
    'componentStackSummary',
    'previousUserExists',
    'ok',
    'reason',
    'origin',
]);

const state = {
    enabled: false,
    investigationUuid: null,
    apiBaseUrl: '',
    apiEndpoint: '/diagnostics/breadcrumbs',
    sessionId: null,
    buffer: [],
    bufferBytes: 0,
    config: { ...DEFAULT_CONFIG },
    lastFlushMs: 0,
    lastFlushFingerprint: null,
    correlationCounter: 0,
    clientBuildCommit: null,
};

function getOrCreateSessionId() {
    try {
        const existing = window.sessionStorage?.getItem('sherwoodDiagSessionId');
        if (existing) return existing;
        const fresh = safeRandomUUID();
        window.sessionStorage?.setItem('sherwoodDiagSessionId', fresh);
        return fresh;
    } catch (_) {
        return safeRandomUUID();
    }
}

function redactData(data) {
    if (!data || typeof data !== 'object') return undefined;
    const out = {};
    for (const key of Object.keys(data)) {
        if (!ALLOWED_DATA_KEYS.has(key)) continue;
        const value = data[key];
        if (value === undefined || value === null) continue;
        if (typeof value === 'object') continue; // allowlist only takes scalars
        if (key === 'url' && typeof value === 'string') {
            out[key] = redactUrl(value);
        } else if (typeof value === 'string' && value.length > 400) {
            out[key] = value.slice(0, 400);
        } else {
            out[key] = value;
        }
    }
    return out;
}

function redactUrl(url) {
    try {
        // Preserve path; drop query except limit/offset.
        const questionIdx = url.indexOf('?');
        const path = questionIdx >= 0 ? url.slice(0, questionIdx) : url;
        const qs = questionIdx >= 0 ? url.slice(questionIdx + 1) : '';
        const keep = [];
        if (qs) {
            for (const pair of qs.split('&')) {
                const eq = pair.indexOf('=');
                const key = eq >= 0 ? pair.slice(0, eq) : pair;
                if (key === 'limit' || key === 'offset') keep.push(pair);
            }
        }
        return keep.length ? `${path}?${keep.join('&')}` : path;
    } catch (_) {
        return '[redacted]';
    }
}

function measureSize(entry) {
    try {
        return JSON.stringify(entry).length;
    } catch (_) {
        return 0;
    }
}

function trimBuffer() {
    while (
        state.buffer.length > state.config.maxBufferSize ||
        state.bufferBytes > state.config.maxBufferBytes
    ) {
        const dropped = state.buffer.shift();
        if (!dropped) break;
        state.bufferBytes -= dropped._bytes || 0;
    }
}

function nextCorrelationId() {
    state.correlationCounter = (state.correlationCounter + 1) & 0xffff;
    return `${state.sessionId || 'nosess'}-${Date.now().toString(36)}-${state.correlationCounter.toString(36)}`;
}

function addBreadcrumb(type, msg, data) {
    if (!state.enabled) return;
    const entry = {
        ts: new Date().toISOString(),
        type: typeof type === 'string' ? type : 'unknown',
        msg: typeof msg === 'string' ? msg.slice(0, 200) : '',
        data: redactData(data),
    };
    entry._bytes = measureSize(entry);
    state.buffer.push(entry);
    state.bufferBytes += entry._bytes;
    trimBuffer();
}

function buildPayload(reason) {
    return {
        investigationUuid: state.investigationUuid,
        sessionId: state.sessionId,
        reason: typeof reason === 'string' ? reason : 'unknown',
        clientBuildCommit: state.clientBuildCommit,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        breadcrumbs: state.buffer.map(({ _bytes, ...rest }) => rest),
    };
}

function fingerprint(payload) {
    const first = payload.breadcrumbs[0];
    const firstTs = first ? first.ts : '-';
    return `${payload.reason}|${firstTs}|${payload.breadcrumbs.length}`;
}

function flush(reason) {
    if (!state.enabled) return;
    if (!state.buffer.length) return;
    const now = Date.now();
    if (state.lastFlushMs && now - state.lastFlushMs < state.config.flushRateLimitMs) {
        // Rate-limited — same window. Drop silently.
        return;
    }
    const payload = buildPayload(reason);
    const fp = fingerprint(payload);
    if (fp === state.lastFlushFingerprint && now - state.lastFlushMs < state.config.flushRateLimitMs * 2) {
        return;
    }
    state.lastFlushMs = now;
    state.lastFlushFingerprint = fp;

    const url = `${state.apiBaseUrl}${state.apiEndpoint}`;
    const body = JSON.stringify(payload);
    const authToken = safeGetAuthToken();

    try {
        // Raw fetch (not axios) so we can't recursively breadcrumb ourselves,
        // and keepalive so the request survives page unload / logout redirects.
        fetch(url, {
            method: 'POST',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                ...(authToken ? { Authorization: authToken } : {}),
            },
            body,
        }).catch(() => {
            // Losing a breadcrumb flush is acceptable; do NOT retry.
        });
    } catch (_) { /* ignore */ }
}

async function initDiagnostics({ investigationUuid, apiBaseUrl, clientBuildCommit } = {}) {
    if (!investigationUuid) return;
    // Re-init is a no-op unless the investigation changed.
    if (state.enabled && state.investigationUuid === investigationUuid) return;

    state.investigationUuid = investigationUuid;
    state.apiBaseUrl = apiBaseUrl || state.apiBaseUrl || inferApiBaseUrl();
    state.sessionId = getOrCreateSessionId();
    state.clientBuildCommit = clientBuildCommit || state.clientBuildCommit;

    try {
        const token = safeGetAuthToken();
        const resp = await fetch(
            `${state.apiBaseUrl}/diagnostics/investigation/${encodeURIComponent(investigationUuid)}/config`,
            {
                method: 'GET',
                headers: token ? { Authorization: token } : {},
            }
        );
        if (!resp.ok) {
            state.enabled = false;
            return;
        }
        const cfg = await resp.json();
        if (!cfg || cfg.breadcrumbsEnabled !== true) {
            state.enabled = false;
            return;
        }
        state.config = {
            maxBufferSize: Number(cfg.maxBufferSize) || DEFAULT_CONFIG.maxBufferSize,
            maxBufferBytes: Number(cfg.maxBufferBytes) || DEFAULT_CONFIG.maxBufferBytes,
            flushRateLimitMs: Number(cfg.flushRateLimitMs) || DEFAULT_CONFIG.flushRateLimitMs,
        };
        state.enabled = true;
    } catch (_) {
        state.enabled = false;
    }
}

function parseJwtSafe(token) {
    try {
        const claims = decodeJwtClaims(token);
        return {
            jwtExp: typeof claims.exp === 'number' ? claims.exp : undefined,
            jwtIat: typeof claims.iat === 'number' ? claims.iat : undefined,
            researcherUuid: typeof claims.uuid === 'string' ? claims.uuid : undefined,
        };
    } catch (_) {
        return {};
    }
}

function isEnabled() {
    return state.enabled;
}

export {
    initDiagnostics,
    addBreadcrumb,
    flush,
    nextCorrelationId,
    parseJwtSafe,
    isEnabled,
};
